/**
 * ScreenshotCaptureOverlayComponent
 *
 * Full-bounds overlay that sits on top of any canvas surface (initially the
 * takeoffs `<ejs-pdfviewer>` wrapper). When [active] flips true, a cross-hair
 * mask dims everything except a user-dragged rectangle. On release, emits a
 * Region to the host which performs the actual html2canvas crop.
 *
 * Z-index 30 — sits above app-canvas-svg-overlay / app-drawing-preview but
 * below toasts and modals.
 *
 * Designed as a *shared* component: it accepts the host element via the
 * [hostElement] input so it can be reused on any surface, not just takeoffs.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';

import { Region } from '../screenshots.types';
import { MIN_REGION_SIZE } from '../screenshots.constants';

@Component({
  standalone: false,
  selector: 'app-screenshot-capture-overlay',
  templateUrl: './screenshot-capture-overlay.component.html',
  styleUrls: ['./screenshot-capture-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('150ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('100ms cubic-bezier(0.4, 0, 1, 1)', style({ opacity: 0 }))]),
    ]),
  ],
})
export class ScreenshotCaptureOverlayComponent implements OnChanges {
  /** When true, overlay is mounted; when false, all state resets. */
  @Input() active = false;

  /**
   * The host element whose bounding rect defines the capture coordinate space.
   * If omitted, the overlay's own bounding rect is used as a fallback.
   */
  @Input() hostElement?: HTMLElement | null;

  /** Emits a Region in host-element-local coordinates when the user completes a drag. */
  @Output() capture = new EventEmitter<Region>();

  /** Emits when the user presses ESC or releases without a valid drag. */
  @Output() cancel = new EventEmitter<void>();

  @ViewChild('overlayRoot', { static: false }) overlayRoot?: ElementRef<HTMLDivElement>;

  // Drag state — kept public so the template can read for the selection box
  public isDragging = false;
  public startPoint = { x: 0, y: 0 };
  public currentPoint = { x: 0, y: 0 };

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active'] && !this.active) {
      this.resetDrag();
    }
  }

  /** Selection rectangle for the template (normalized so width/height are positive). */
  get selectionRect(): Region | null {
    if (!this.isDragging) return null;
    return this.normalize(this.startPoint, this.currentPoint);
  }

  /** Live "W × H px" badge content. */
  get selectionLabel(): string {
    const r = this.selectionRect;
    if (!r) return '';
    return `${Math.round(r.width)} × ${Math.round(r.height)} px`;
  }

  onMouseDown(event: MouseEvent): void {
    if (!this.active) return;
    if (event.button !== 0) return; // left-button only
    event.preventDefault();
    const p = this.toLocal(event);
    this.startPoint = p;
    this.currentPoint = p;
    this.isDragging = true;
    this.cdr.markForCheck();
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.currentPoint = this.toLocal(event);
    this.cdr.markForCheck();
  }

  onMouseUp(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.currentPoint = this.toLocal(event);
    const region = this.normalize(this.startPoint, this.currentPoint);
    this.resetDrag();
    if (region.width >= MIN_REGION_SIZE && region.height >= MIN_REGION_SIZE) {
      this.capture.emit(region);
    } else {
      this.cancel.emit();
    }
  }

  onMouseLeave(): void {
    // If user drags off the overlay then releases outside, treat as cancel.
    if (this.isDragging) {
      this.resetDrag();
      this.cancel.emit();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.active) return;
    this.resetDrag();
    this.cancel.emit();
  }

  /** Convert a viewport mouse event to host-element-local coordinates. */
  private toLocal(event: MouseEvent): { x: number; y: number } {
    const rect = this.getHostRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  /** Resolve the capture coordinate space (host element or overlay self). */
  private getHostRect(): DOMRect {
    const el = this.hostElement || this.overlayRoot?.nativeElement;
    if (!el) {
      return new DOMRect(0, 0, window.innerWidth, window.innerHeight);
    }
    return el.getBoundingClientRect();
  }

  /** Normalize 2 points into a positive-width/height region. */
  private normalize(a: { x: number; y: number }, b: { x: number; y: number }): Region {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    return {
      x,
      y,
      width: Math.abs(a.x - b.x),
      height: Math.abs(a.y - b.y),
    };
  }

  private resetDrag(): void {
    this.isDragging = false;
    this.startPoint = { x: 0, y: 0 };
    this.currentPoint = { x: 0, y: 0 };
    this.cdr.markForCheck();
  }
}
